/**
 * URL pattern combobox: one shared row shape ({@link UrlPatternDropdownOption}) and a single ordered
 * {@link URL_PATTERN_DROPDOWN_SYNTAX_OPTIONS_SOURCE} list for the main syntax menu (presets + tokens + nested parents).
 * Schema-driven field/taxonomy UID rows reuse the same shape with `category: 'nested'` and `nestedRole: 'leaf'`.
 */

import {
  URL_PREDEFINED_FULL_PATTERNS,
  URL_PREDEFINED_PATTERN_LABELS,
  fieldPatternToken,
  getUrlPatternSyntaxTokenIcon,
  taxonomyPatternToken,
  urlPatternSuggestionMatches,
  type TaxonomyUidOption,
  type UrlPatternSuggestionRowIcon
} from './urlManagementV2Utils'

/** Help copy used to match the "field" drill-down row when filtering. */
export const URL_PATTERN_FIELD_SYNTAX_HELP_TEXT = 'Field value (for example, author)'

/** Info banner copy for the field UID submenu. */
export const URL_PATTERN_FIELD_SUBMENU_INFO =
  'Select a supported root-level text field to add to the URL pattern. Markdown and Rich Text Editor fields are not supported.'

/** How the row behaves in the dropdown UI. */
export type UrlPatternDropdownOptionCategory = 'preset' | 'syntax' | 'nested'

/** One row for every URL pattern menu line (syntax list + field/taxonomy submenus). */
export type UrlPatternDropdownOption = {
  id: string
  category: UrlPatternDropdownOptionCategory
  /** Primary label in the menu row */
  displayName: string
  /** Secondary line (description, full path for presets, token preview for leaves) */
  subtitle: string
  /** Label passed to {@link applyUrlPatternSuggestion} (token / field display / taxonomy uid) */
  suggestionLabel: string
  /** For `preset`, full pattern value; for `syntax` / nested `leaf`, insert fragment; unused for nested `parent` */
  insertToken: string
  rowIcon: UrlPatternSuggestionRowIcon
  /**
   * When `category === 'nested'`: `parent` opens the field or taxonomy UID submenu; `leaf` is a schema-driven UID row.
   */
  nestedRole?: 'parent' | 'leaf'
  /** Which submenu a nested row belongs to. */
  nestedSubmenu?: 'field' | 'taxonomy'
  /** Set on nested `leaf` rows — field UID or taxonomy UID (used for stable keys and test ids). */
  schemaEntityUid?: string
}

type UrlPatternDropdownSyntaxSourceRow = UrlPatternDropdownOption & {
  /** If set, this row is included in the syntax template only when the content type has a taxonomy field. */
  omitUnlessSchemaHasTaxonomy?: boolean
}

function slugForPresetTestId(displayName: string): string {
  return displayName.replace(/\s+/g, '-').toLowerCase()
}

function syntaxTokenOption(
  displayName: string,
  insertToken: string,
  subtitle: string
): UrlPatternDropdownSyntaxSourceRow {
  return {
    id: `syntax-token:${insertToken}`,
    category: 'syntax',
    displayName,
    subtitle,
    suggestionLabel: displayName,
    insertToken,
    rowIcon: getUrlPatternSyntaxTokenIcon(insertToken)
  }
}

/**
 * Ordered source list for the main syntax dropdown: presets (from shared URL constants), then tokens and nested parents.
 * Edit this array to add/remove/reorder options. Taxonomy parent row is gated by {@link omitUnlessSchemaHasTaxonomy}.
 */
export const URL_PATTERN_DROPDOWN_SYNTAX_OPTIONS_SOURCE: readonly UrlPatternDropdownSyntaxSourceRow[] = [
  ...(URL_PREDEFINED_FULL_PATTERNS as readonly string[]).map((insertToken): UrlPatternDropdownSyntaxSourceRow => {
    const displayName = URL_PREDEFINED_PATTERN_LABELS[insertToken]
    return {
      id: `preset:${insertToken}`,
      category: 'preset',
      displayName,
      subtitle: insertToken,
      suggestionLabel: displayName,
      insertToken,
      rowIcon: 'CalendarBlank'
    }
  }),
  syntaxTokenOption('title', ':title', 'Title of the entry'),
  syntaxTokenOption('unique_id', ':unique_id', 'Unique ID of the entry'),
  syntaxTokenOption('locale', ':locale', 'Locale code for the entry'),
  syntaxTokenOption('locale_name', ':locale_name', 'Locale name of the entry'),
  {
    id: 'syntax-drilldown-field',
    category: 'nested',
    nestedRole: 'parent',
    nestedSubmenu: 'field',
    displayName: 'field',
    subtitle: URL_PATTERN_FIELD_SYNTAX_HELP_TEXT,
    suggestionLabel: 'field',
    insertToken: '',
    rowIcon: 'EntriesSecondary'
  },
  {
    id: 'syntax-drilldown-taxonomy',
    category: 'nested',
    nestedRole: 'parent',
    nestedSubmenu: 'taxonomy',
    displayName: 'taxonomy',
    subtitle: 'Taxonomy of the entry (e.g., blog)',
    suggestionLabel: 'taxonomy',
    insertToken: '',
    rowIcon: 'Taxonomy',
    omitUnlessSchemaHasTaxonomy: true
  },
  syntaxTokenOption('year', ':year', 'Year of entry creation (for example, 2025)'),
  syntaxTokenOption('year_short', ':year_short', 'Short year format (for example, 25)'),
  syntaxTokenOption('month', ':month', 'Month in MM format (for example, 03)'),
  syntaxTokenOption('monthname', ':monthname', 'Month name (for example, March)'),
  syntaxTokenOption('monthname_short', ':monthname_short', 'Short month name (for example, Jan)'),
  syntaxTokenOption('day', ':day', 'Day in DD format'),
  syntaxTokenOption('year-month-day', ':year-:month-:day', 'Date of entry creation in YYYY-MM-DD format')
]

function stripSourceMetadata(row: UrlPatternDropdownSyntaxSourceRow): UrlPatternDropdownOption {
  const { omitUnlessSchemaHasTaxonomy: _omit, ...rest } = row
  return rest
}

/**
 * Syntax menu template (not filtered). Drops taxonomy parent when the schema has no taxonomy field.
 */
export function buildUrlPatternSyntaxMenuTemplate(includeTaxonomySyntaxRow: boolean): UrlPatternDropdownOption[] {
  return URL_PATTERN_DROPDOWN_SYNTAX_OPTIONS_SOURCE.filter(
    (row) => !row.omitUnlessSchemaHasTaxonomy || includeTaxonomySyntaxRow
  ).map(stripSourceMetadata)
}

function isFieldSyntaxRowVisible(
  filterQuery: string,
  rootTextFields: Array<{ uid: string; display_name: string }>
): boolean {
  if (rootTextFields.length === 0) return false
  return (
    urlPatternSuggestionMatches(filterQuery, 'field', URL_PATTERN_FIELD_SYNTAX_HELP_TEXT) ||
    rootTextFields.some((f) =>
      urlPatternSuggestionMatches(filterQuery, f.uid, f.display_name, fieldPatternToken(f.uid))
    )
  )
}

function isTaxonomySyntaxRowVisible(
  filterQuery: string,
  hasTaxonomyField: boolean,
  taxonomyUidOptions: TaxonomyUidOption[]
): boolean {
  if (!hasTaxonomyField) return false
  return (
    urlPatternSuggestionMatches(filterQuery, 'taxonomy', 'taxonomy_uid') ||
    taxonomyUidOptions.some((t) =>
      urlPatternSuggestionMatches(filterQuery, t.taxonomy_uid, taxonomyPatternToken(t.taxonomy_uid))
    )
  )
}

export type UrlPatternSyntaxMenuFilterContext = {
  filterQuery: string
  hasTaxonomyField: boolean
  rootTextFields: Array<{ uid: string; display_name: string }>
  taxonomyUidOptions: TaxonomyUidOption[]
}

function isSyntaxMenuRowVisible(row: UrlPatternDropdownOption, ctx: UrlPatternSyntaxMenuFilterContext): boolean {
  const { filterQuery, hasTaxonomyField, rootTextFields, taxonomyUidOptions } = ctx
  switch (row.category) {
    case 'preset':
      return urlPatternSuggestionMatches(filterQuery, row.displayName, row.insertToken)
    case 'syntax':
      return urlPatternSuggestionMatches(filterQuery, row.displayName, row.insertToken, row.subtitle)
    case 'nested':
      if (row.nestedRole === 'parent' && row.nestedSubmenu === 'field') {
        return isFieldSyntaxRowVisible(filterQuery, rootTextFields)
      }
      if (row.nestedRole === 'parent' && row.nestedSubmenu === 'taxonomy') {
        return isTaxonomySyntaxRowVisible(filterQuery, hasTaxonomyField, taxonomyUidOptions)
      }
      return false
  }
}

export function getVisibleUrlPatternSyntaxMenuRows(ctx: UrlPatternSyntaxMenuFilterContext): UrlPatternDropdownOption[] {
  const template = buildUrlPatternSyntaxMenuTemplate(ctx.hasTaxonomyField)
  return template.filter((row) => isSyntaxMenuRowVisible(row, ctx))
}

/** `data-test-id` for a row in the main syntax menu (not used for nested leaf rows in submenus). */
export function getUrlPatternSyntaxMenuRowTestId(row: UrlPatternDropdownOption): string {
  switch (row.category) {
    case 'preset':
      return `cs-ct-field-url-v2-preset-${slugForPresetTestId(row.displayName)}`
    case 'nested':
      if (row.nestedRole === 'parent' && row.nestedSubmenu === 'field') {
        return 'cs-ct-field-url-v2-syntax-field-parent'
      }
      if (row.nestedRole === 'parent' && row.nestedSubmenu === 'taxonomy') {
        return 'cs-ct-field-url-v2-syntax-taxonomy-parent'
      }
      return `cs-ct-field-url-v2-syntax-${row.displayName.replace(/\s+/g, '-')}`
    case 'syntax':
      return `cs-ct-field-url-v2-syntax-${row.displayName.replace(/\s+/g, '-')}`
  }
}

export function buildVisibleSchemaFieldMenuRows(
  rootTextFields: Array<{ uid: string; display_name: string }>,
  filterQuery: string
): UrlPatternDropdownOption[] {
  return rootTextFields
    .filter((f) => urlPatternSuggestionMatches(filterQuery, f.uid, f.display_name, fieldPatternToken(f.uid)))
    .map((f) => ({
      id: `field-option:${f.uid}`,
      category: 'nested' as const,
      nestedRole: 'leaf' as const,
      nestedSubmenu: 'field' as const,
      schemaEntityUid: f.uid,
      displayName: f.display_name,
      subtitle: fieldPatternToken(f.uid),
      suggestionLabel: f.display_name,
      insertToken: fieldPatternToken(f.uid),
      rowIcon: 'EntriesSecondary' as const
    }))
}

export function buildVisibleSchemaTaxonomyMenuRows(
  taxonomyUidOptions: TaxonomyUidOption[],
  filterQuery: string
): UrlPatternDropdownOption[] {
  return taxonomyUidOptions
    .filter((t) => urlPatternSuggestionMatches(filterQuery, t.taxonomy_uid, taxonomyPatternToken(t.taxonomy_uid)))
    .map((t) => ({
      id: `taxonomy-option:${t.taxonomy_uid}`,
      category: 'nested' as const,
      nestedRole: 'leaf' as const,
      nestedSubmenu: 'taxonomy' as const,
      schemaEntityUid: t.taxonomy_uid,
      displayName: t.taxonomy_uid,
      subtitle: taxonomyPatternToken(t.taxonomy_uid),
      suggestionLabel: t.taxonomy_uid,
      insertToken: taxonomyPatternToken(t.taxonomy_uid),
      rowIcon: 'Taxonomy' as const
    }))
}

export function getUrlPatternFieldMenuRowTestId(fieldUid: string): string {
  return `cs-ct-field-url-v2-field-${fieldUid}`
}

export function getUrlPatternTaxonomyMenuRowTestId(taxonomyUid: string): string {
  return `cs-ct-field-url-v2-taxonomy-${taxonomyUid}`
}

/** @deprecated Use {@link UrlPatternDropdownOption} */
export type UrlPatternSyntaxMenuRowModel = UrlPatternDropdownOption
